import { Check, ChevronsUpDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const RELATION_LABEL: Record<string, string> = {
  MOTHER: 'Mother',
  FATHER: 'Father',
  GUARDIAN: 'Guardian',
  SIBLING: 'Sibling',
  SPOUSE: 'Spouse',
  OTHER: 'Relative',
};

function initialOf(name: string | undefined): string {
  return name?.[0]?.toUpperCase() ?? '?';
}

/** "Space switcher" at the top of the sidebar: lets a parent/guardian move
 * between their own profile and their relatives' profiles. Renders nothing for
 * clients without a household, so single-profile accounts see no change. */
export function ClientSwitcher(): JSX.Element | null {
  const { client, household, activeMember, switchClient } = useAuth();
  const navigate = useNavigate();

  if (!client || household.length === 0) return null;

  const activeName = activeMember
    ? `${activeMember.firstName} ${activeMember.lastName}`
    : `${client.firstName} ${client.lastName}`;
  const activeSub = activeMember ? activeMember.clientCode : 'My profile';

  const select = (id: string | null): void => {
    void switchClient(id).then(() => navigate('/app'));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Switch profile"
          className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left bg-sidebar-accent/60 hover:bg-sidebar-accent transition-colors group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-0"
        >
          <Avatar size="sm" className="bg-sidebar-primary/20 shrink-0">
            <AvatarFallback className="bg-transparent text-sidebar-primary font-display font-medium">
              {initialOf(activeMember?.firstName ?? client.firstName)}
            </AvatarFallback>
          </Avatar>
          <span className="flex-1 min-w-0 group-data-[state=collapsed]:hidden">
            <span className="block text-sm font-medium text-sidebar-foreground truncate">
              {activeName}
            </span>
            <span className="block text-xs text-sidebar-foreground/45 truncate">{activeSub}</span>
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-sidebar-foreground/40 shrink-0 group-data-[state=collapsed]:hidden" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="start" className="w-64">
        <DropdownMenuLabel>Switch profile</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => select(null)}>
          <Avatar size="sm" className="bg-sidebar-primary/20 shrink-0">
            <AvatarFallback className="bg-transparent font-display font-medium">
              {initialOf(client.firstName)}
            </AvatarFallback>
          </Avatar>
          <span className="flex-1 min-w-0">
            <span className="block text-sm truncate">
              {client.firstName} {client.lastName}
            </span>
            <span className="block text-xs text-muted-foreground">Me</span>
          </span>
          {activeMember === null && <Check className="ml-auto" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {household.map((m) => (
          <DropdownMenuItem key={m.clientId} onClick={() => select(m.clientId)}>
            <Avatar size="sm" className="shrink-0">
              <AvatarFallback className="font-display font-medium">
                {initialOf(m.firstName)}
              </AvatarFallback>
            </Avatar>
            <span className="flex-1 min-w-0">
              <span className="block text-sm truncate">
                {m.firstName} {m.lastName}
              </span>
              <span className="block text-xs text-muted-foreground">
                {RELATION_LABEL[m.relationType] ?? 'Relative'} · {m.clientCode}
              </span>
            </span>
            {activeMember?.clientId === m.clientId && <Check className="ml-auto" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
